// @ts-check

/**
 * @file Renders a reusable pagination control.
 */

import PropTypes from "prop-types"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, View} from "react-native"
import TextInput from "./text-input"

import useThemeColors, {useThemeName} from "../shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {import("react").ComponentProps<typeof FontAwesome6>["name"]} FontAwesome6Name */
/** @type {Record<string, object>} */
const styles = {}

const MAX_PAGE_LINKS = 10

/**
 * Sliding page window. Always renders up to `MAX_PAGE_LINKS` consecutive
 * pages centered on `current`; shifts the window at the edges instead of
 * padding with placeholders so the list is always clickable.
 * @param {number} current 1-based current page.
 * @param {number} total Total number of pages.
 * @returns {number[]} Ordered list of page numbers to render.
 */
function pageWindow(current, total) {
  if (total <= MAX_PAGE_LINKS) {
    return Array.from({length: total}, (_, i) => i + 1)
  }

  const half = Math.floor(MAX_PAGE_LINKS / 2)
  let start = Math.max(1, current - half)
  const end = Math.min(total, start + MAX_PAGE_LINKS - 1)

  if (end - start + 1 < MAX_PAGE_LINKS) {
    start = Math.max(1, end - MAX_PAGE_LINKS + 1)
  }

  return Array.from({length: end - start + 1}, (_, i) => start + i)
}

/** @typedef {{[key: string]: unknown, disabled?: boolean, onPageChange: (...args: unknown[]) => unknown, page: number, pageSize: number, testID: string, totalCount: number}} PaginationProps */

/**
 * @typedef {object} PaginationState
 * @property {string} jumpValue Value entered in the jump-to-page input.
 */

/**
 * Shared pagination control — First / Prev / numbered page links /
 * Next / Last, plus a jump-to-page input when `totalPages` exceeds the
 * visible window. Hides itself when there's only one page of data.
 * @augments {ShapeComponent<PaginationProps, PaginationState>}
 */
class Pagination extends ShapeComponent {
  static propTypes = propTypesExact({
    disabled: PropTypes.bool,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    testID: PropTypes.string.isRequired,
    totalCount: PropTypes.number.isRequired
  })

  static defaultProps = {
    disabled: false
  }

  /** @type {PaginationState} */
  state = {
    jumpValue: ""
  }

  /** @type {Map<number, () => void>} */
  pagePressHandlers = new Map()
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Calculates the total page count.
   * @returns {number} Total pages with a minimum of one.
   */
  totalPages() {
    const {pageSize, totalCount} = this.p
    if (!pageSize || pageSize <= 0) return 1
    return Math.max(1, Math.ceil(totalCount / pageSize))
  }

  /**
   * Renders the pagination control.
   * @returns {ReactNode} Pagination controls or null for a single page.
   */
  render() {
    const {disabled, page, testID} = this.p
    const totalPages = this.totalPages()

    if (totalPages <= 1) return null

    const visiblePages = pageWindow(page, totalPages)
    const isFirstPage = page <= 1
    const isLastPage = page >= totalPages
    const showJump = totalPages > MAX_PAGE_LINKS

    return (
      <View
        style={styles.container ||= {
          alignItems: "center",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 6,
          justifyContent: "center",
          marginVertical: 16
        }}
        testID={testID}
      >
        {this.renderStepButton(
          "First",
          "angles-left",
          this.tt.onFirstPress,
          Boolean(disabled) || isFirstPage,
          `${testID}/firstButton`
        )}
        {this.renderStepButton(
          "Prev",
          "chevron-left",
          this.tt.onPrevPress,
          Boolean(disabled) || isFirstPage,
          `${testID}/prevButton`
        )}

        {visiblePages.map((pageNumber) => this.renderPageButton(pageNumber))}

        {this.renderStepButton(
          "Next",
          "chevron-right",
          this.tt.onNextPress,
          Boolean(disabled) || isLastPage,
          `${testID}/nextButton`
        )}
        {this.renderStepButton(
          "Last",
          "angles-right",
          this.tt.onLastPress,
          Boolean(disabled) || isLastPage,
          `${testID}/lastButton`
        )}

        {showJump && this.renderJumpInput(totalPages)}

        {this.renderSummary(page, totalPages)}
      </View>
    )
  }

  /**
   * Renders a first, previous, next, or last step button.
   * @param {string} label Visible text for the control.
   * @param {FontAwesome6Name} iconName Icon shown next to the label.
   * @param {() => void} onPress Callback invoked on press.
   * @param {boolean} stepDisabled Whether the button is disabled.
   * @param {string} stepTestID Test identifier for the button.
   * @returns {ReactNode} Rendered step control.
   */
  renderStepButton(label, iconName, onPress, stepDisabled, stepTestID) {
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    return (
      <Pressable
        disabled={stepDisabled}
        key={stepTestID}
        onPress={onPress}
        style={styles[`step-${themeName}-${stepDisabled}`] ||= {
          alignItems: "center",
          backgroundColor: stepDisabled ? colors.disabledBackground : colors.surface,
          borderColor: stepDisabled ? colors.border : colors.borderStrong,
          borderRadius: 6,
          borderWidth: 1,
          flexDirection: "row",
          justifyContent: "center",
          minHeight: 36,
          paddingHorizontal: 10,
          paddingVertical: 7
        }}
        testID={stepTestID}
      >
        <View
          style={styles.stepIcon ||= {
            marginRight: 8,
            width: 12
          }}
          testID={`${stepTestID}/icon`}
        >
          <FontAwesome6
            color={stepDisabled ? colors.disabledText : colors.text}
            name={iconName}
            size={12}
          />
        </View>
        <Text
          style={styles[`stepLabel-${themeName}-${stepDisabled}`] ||= {
            color: stepDisabled ? colors.disabledText : colors.text,
            fontWeight: 600
          }}
          testID={`${stepTestID}/label`}
        >
          {label}
        </Text>
      </Pressable>
    )
  }

  /**
   * Renders a numbered page button.
   * @param {number} pageNumber Page number to render.
   * @returns {ReactNode} Rendered numbered control.
   */
  renderPageButton(pageNumber) {
    const {disabled, page, testID} = this.p
    const active = pageNumber === page
    const buttonDisabled = Boolean(disabled) || active
    const colors = this.tt.colors
    const themeName = this.tt.themeName

    return (
      <Pressable
        disabled={buttonDisabled}
        key={pageNumber}
        onPress={this.pagePressHandler(pageNumber)}
        style={styles[`page-${themeName}-${active}-${Boolean(disabled)}`] ||= {
          alignItems: "center",
          backgroundColor: active ? colors.primary : colors.surface,
          borderColor: active ? colors.primary : colors.borderStrong,
          borderRadius: 6,
          borderWidth: 1,
          justifyContent: "center",
          minHeight: 36,
          minWidth: 36,
          paddingHorizontal: 10,
          paddingVertical: 7
        }}
        testID={`${testID}/page-${pageNumber}`}
      >
        <Text
          style={styles[`pageLabel-${themeName}-${active}-${Boolean(disabled)}`] ||= {
            color: active ? colors.onPrimary : disabled ? colors.disabledText : colors.text,
            fontWeight: active ? 700 : 500
          }}
          testID={`${testID}/page-${pageNumber}/label`}
        >
          {pageNumber}
        </Text>
      </Pressable>
    )
  }

  /**
   * Renders the jump-to-page input.
   * @param {number} totalPages Total available pages.
   * @returns {ReactNode} Jump input row.
   */
  renderJumpInput(totalPages) {
    const {disabled, testID} = this.p
    const colors = this.tt.colors
    const themeName = this.tt.themeName

    return (
      <View
        style={styles.jumpRow ||= {
          alignItems: "center",
          flexDirection: "row",
          gap: 6,
          marginLeft: 8
        }}
      >
        <Text
          style={styles[`jumpLabel-${themeName}`] ||= {
            color: colors.textMuted,
            fontWeight: 500
          }}
          testID={`${testID}/jumpLabel`}
        >
          Go to:
        </Text>
        <TextInput
          editable={!disabled}
          inputMode="numeric"
          keyboardType="number-pad"
          onChangeText={this.tt.onJumpChangeText}
          onSubmitEditing={this.tt.onJumpSubmit}
          placeholder={String(totalPages)}
          returnKeyType="go"
          style={styles.jumpInput ||= {
            minHeight: 36,
            minWidth: 64,
            paddingHorizontal: 10,
            paddingVertical: 7,
            textAlign: "center"
          }}
          testID={`${testID}/jumpInput`}
          value={this.s.jumpValue}
        />
      </View>
    )
  }

  /**
   * Renders the current page summary.
   * @param {number} page Current page number.
   * @param {number} totalPages Total available pages.
   * @returns {ReactNode} Page summary text.
   */
  renderSummary(page, totalPages) {
    const {testID} = this.p

    return (
      <Text
        style={styles[`summary-${this.tt.themeName}`] ||= {
          color: this.tt.colors.textMuted,
          fontSize: 13,
          marginLeft: 8
        }}
        testID={`${testID}/summary`}
      >
        Page {page} of {totalPages}
      </Text>
    )
  }

  /**
   * Returns a cached page press handler.
   * @param {number} pageNumber Target page number.
   * @returns {() => void} Cached handler for the requested page.
   */
  pagePressHandler(pageNumber) {
    let handler = this.pagePressHandlers.get(pageNumber)

    if (!handler) {
      handler = () => this.emitChange(pageNumber)
      this.pagePressHandlers.set(pageNumber, handler)
    }

    return handler
  }

  /**
   * Emits a bounded page change.
   * @param {number} pageNumber Requested page number.
   * @returns {void} Nothing.
   */
  emitChange(pageNumber) {
    const totalPages = this.totalPages()
    const bounded = Math.max(1, Math.min(totalPages, pageNumber))

    if (bounded !== this.p.page) {
      this.p.onPageChange(bounded)
    }
  }

  /**
   * Moves to the first page.
   * @returns {void} No return value.
   */
  onFirstPress = () => this.emitChange(1)

  /**
   * Moves to the previous page.
   * @returns {void} No return value.
   */
  onPrevPress = () => this.emitChange(this.p.page - 1)

  /**
   * Moves to the next page.
   * @returns {void} No return value.
   */
  onNextPress = () => this.emitChange(this.p.page + 1)

  /**
   * Moves to the last page.
   * @returns {void} No return value.
   */
  onLastPress = () => this.emitChange(this.totalPages())

  /**
   * Stores the numeric jump input value.
   * @param {string} value Raw input value.
   * @returns {void} Nothing.
   */
  onJumpChangeText = (value) => {
    this.s.jumpValue = value.replace(/[^0-9]/g, "")
  }

  /**
   * Applies the jump input value.
   * @returns {void} Nothing.
   */
  onJumpSubmit = () => {
    const parsed = parseInt(this.s.jumpValue, 10)

    if (Number.isFinite(parsed)) {
      this.emitChange(parsed)
    }

    this.s.jumpValue = ""
  }
}


/** @type {NamedExoticComponent<PaginationProps>} */
const PaginationComponent = memo(shapeComponent(Pagination))

export default PaginationComponent
